import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { cable } from '../../cable.js'
import { t } from '../../i18n.js'
import { isoToDisplayDate as fmtDate } from '../utils/dateFormat.js'

const START_DATE_MODES = [ 'valuation_date', 'specified_date' ]
const END_DATE_MODES = [ 'none', 'specified_date' ]

const stringOrNil = (value) => (value === '' || value === null || value === undefined ? null : value)

function formToState(props) {
  return {
    enabled: Boolean(props.enabled),
    rate: props.rate ?? '0',
    startDateMode: props.startDateMode || 'valuation_date',
    specifiedStartDate: props.specifiedStartDate || '',
    endDateMode: props.endDateMode || 'none',
    specifiedEndDate: props.specifiedEndDate || ''
  }
}

export default function CreditLossTab(props) {
  const { valuationId, updateUrl, csrfToken, readOnly = false, valuationDate, translations } = props

  const [form, setForm] = useState(() => formToState(props))
  const [dirty, setDirty] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')
  const [savedAt, setSavedAt] = useState(null)
  const lastRequestIdRef = useRef(null)
  const dirtyRef = useRef(false)

  useEffect(() => { dirtyRef.current = dirty }, [dirty])

  const setField = useCallback((field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }))
    setDirty(true)
    setSavedAt(null)
  }, [])

  // Only enforce hard rules while the feature is enabled (mirrors the prototype:
  // a disabled assumption never shows rate/date errors).
  const validationErrors = useMemo(() => {
    if (!form.enabled) return {}
    const errors = {}
    const rate = Number(form.rate)
    if (form.rate !== '' && (Number.isNaN(rate) || rate < 0 || rate > 100)) {
      errors.rate = t('errors.rate_range', translations)
    }
    if (form.startDateMode === 'specified_date' && !form.specifiedStartDate) {
      errors.specifiedStartDate = t('errors.start_required', translations)
    }
    if (form.endDateMode === 'specified_date' && !form.specifiedEndDate) {
      errors.specifiedEndDate = t('errors.end_required', translations)
    }
    if (form.startDateMode === 'specified_date' && form.endDateMode === 'specified_date' &&
        form.specifiedStartDate && form.specifiedEndDate && form.specifiedEndDate < form.specifiedStartDate) {
      errors.specifiedEndDate = t('errors.end_before_start', translations)
    }
    return errors
  }, [form, translations])

  // Non-blocking advisory — never gates saving.
  const softWarning = form.enabled && Number(form.rate) > 10

  const canSave = !readOnly && !saving && Object.keys(validationErrors).length === 0

  // Live cross-client sync: skip our own echo, and never clobber unsaved edits.
  useEffect(() => {
    if (!valuationId) return undefined
    const subscription = cable.subscriptions.create(
      { channel: 'CreditLossChannel', valuation_id: valuationId },
      {
        received: (data) => {
          if (!data || data.request_id === lastRequestIdRef.current) return
          if (dirtyRef.current || !data.data) return
          setForm(formToState({
            enabled: data.data.enabled,
            rate: data.data.rate,
            startDateMode: data.data.start_date_mode,
            specifiedStartDate: data.data.specified_start_date,
            endDateMode: data.data.end_date_mode,
            specifiedEndDate: data.data.specified_end_date
          }))
        }
      }
    )
    return () => subscription.unsubscribe()
  }, [valuationId])

  const save = useCallback(async () => {
    if (!canSave) return
    setSaving(true)
    setSaveError('')
    const requestId = crypto.randomUUID()
    lastRequestIdRef.current = requestId
    const payload = {
      request_id: requestId,
      enabled: form.enabled,
      rate: form.rate === '' ? 0 : Number(form.rate),
      start_date_mode: form.startDateMode,
      specified_start_date: form.startDateMode === 'specified_date' ? stringOrNil(form.specifiedStartDate) : null,
      end_date_mode: form.endDateMode,
      specified_end_date: form.endDateMode === 'specified_date' ? stringOrNil(form.specifiedEndDate) : null
    }
    try {
      const response = await fetch(updateUrl, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
        body: JSON.stringify(payload)
      })
      const body = await response.json()
      if (body.success) {
        setDirty(false)
        setSavedAt(Date.now())
      } else {
        // t() returns the lookup path itself on a miss (never falsy), so detect
        // an unmapped error code by comparing against the path and fall back.
        const key = `errors.${body.error}`
        const message = t(key, translations)
        setSaveError(message === key ? t('errors.save_failed', translations) : message)
      }
    } catch (e) {
      setSaveError(t('errors.save_failed', translations))
    } finally {
      setSaving(false)
    }
  }, [canSave, form, updateUrl, csrfToken, translations])

  const activeSummary = useMemo(() => {
    if (!form.enabled) return null
    const rate = `${Number(form.rate || 0).toFixed(2)}%`
    const start = form.startDateMode === 'specified_date' && form.specifiedStartDate
      ? fmtDate(form.specifiedStartDate)
      : (valuationDate ? fmtDate(valuationDate) : t('summary.valuation_date', translations))
    const end = form.endDateMode === 'specified_date' && form.specifiedEndDate
      ? fmtDate(form.specifiedEndDate)
      : t('summary.open_ended', translations)
    return t('summary.active', translations, { rate, start, end })
  }, [form, valuationDate, translations])

  const invalid = (field) => (form.enabled && validationErrors[field] ? ' is-invalid' : '')

  return (
    <div className="credit-loss-tab">
      <div className="d-flex justify-content-between align-items-start mb-3">
        <p className="text-muted mb-0">{t('subtitle', translations)}</p>
        <span className={`badge ${form.enabled ? 'bg-success' : 'bg-secondary'}`}>
          {form.enabled ? t('status.enabled', translations) : t('status.disabled', translations)}
        </span>
      </div>

      {saveError && <div className="alert alert-danger">{saveError}</div>}
      {savedAt && !dirty && <div className="alert alert-success py-2">{t('saved', translations)}</div>}

      <div className="row g-3">
        <div className="col-12">
          <div className="form-check">
            <input
              type="checkbox"
              className="form-check-input"
              id="credit-loss-enabled"
              checked={form.enabled}
              disabled={readOnly}
              onChange={(e) => setField('enabled', e.target.checked)}
            />
            <label className="form-check-label" htmlFor="credit-loss-enabled">
              {t('enable_label', translations)}
            </label>
          </div>
        </div>

        <div className="col-md-4">
          <label className="form-label" htmlFor="credit-loss-rate">{t('rate_label', translations)}</label>
          <input
            type="number"
            step="0.01"
            min="0"
            max="100"
            id="credit-loss-rate"
            className={`form-control${invalid('rate')}`}
            value={form.rate}
            disabled={readOnly || !form.enabled}
            onChange={(e) => setField('rate', e.target.value)}
          />
          <div className="form-text">{t('rate_help', translations)}</div>
          {form.enabled && validationErrors.rate && <div className="invalid-feedback">{validationErrors.rate}</div>}
          {softWarning && <div className="form-text text-warning">{t('warning_high_rate', translations)}</div>}
        </div>

        <div className="col-md-4">
          <label className="form-label" htmlFor="credit-loss-start-mode">{t('start_date_label', translations)}</label>
          <select
            id="credit-loss-start-mode"
            className="form-select"
            value={form.startDateMode}
            disabled={readOnly || !form.enabled}
            onChange={(e) => setField('startDateMode', e.target.value)}
          >
            {START_DATE_MODES.map((mode) => (
              <option key={mode} value={mode}>{t(`start_modes.${mode}`, translations)}</option>
            ))}
          </select>
          {form.enabled && form.startDateMode === 'specified_date' && (
            <input
              type="date"
              className={`form-control mt-2${invalid('specifiedStartDate')}`}
              value={form.specifiedStartDate}
              disabled={readOnly}
              onChange={(e) => setField('specifiedStartDate', e.target.value)}
            />
          )}
          {form.enabled && validationErrors.specifiedStartDate && (
            <div className="invalid-feedback d-block">{validationErrors.specifiedStartDate}</div>
          )}
        </div>

        <div className="col-md-4">
          <label className="form-label" htmlFor="credit-loss-end-mode">{t('end_date_label', translations)}</label>
          <select
            id="credit-loss-end-mode"
            className="form-select"
            value={form.endDateMode}
            disabled={readOnly || !form.enabled}
            onChange={(e) => setField('endDateMode', e.target.value)}
          >
            {END_DATE_MODES.map((mode) => (
              <option key={mode} value={mode}>{t(`end_modes.${mode}`, translations)}</option>
            ))}
          </select>
          {form.enabled && form.endDateMode === 'specified_date' && (
            <input
              type="date"
              className={`form-control mt-2${invalid('specifiedEndDate')}`}
              value={form.specifiedEndDate}
              disabled={readOnly}
              onChange={(e) => setField('specifiedEndDate', e.target.value)}
            />
          )}
          {form.enabled && validationErrors.specifiedEndDate && (
            <div className="invalid-feedback d-block">{validationErrors.specifiedEndDate}</div>
          )}
        </div>
      </div>

      {activeSummary && <p className="text-muted mt-3 mb-0">{activeSummary}</p>}

      {!readOnly && (
        <div className="mt-3">
          <button type="button" className="btn btn-primary" onClick={save} disabled={!canSave}>
            {saving ? t('saving', translations) : t('save', translations)}
          </button>
        </div>
      )}
    </div>
  )
}
